// Статичные настройки окна «Контакты»: сегменты (подписи и иконки), коды стран, тексты пустого состояния и подсказки.
// Этапы, счётчики и сами контакты приходят с сервера (src/flash/contacts-api.ts).
import type { Segment, Stage } from "./contacts-api";
import { locale, t, tn } from "@/lib/i18n";

export const contactSegments: { id: Segment; label: string; icon: string; hint: string }[] = [
  { id: "fresh", label: t("Созданы < 7 дней"), icon: "ri-user-add-line", hint: t("Контакты, которых добавили на этой неделе") },
  { id: "sleeping", label: t("Неактивны > 3 месяцев"), icon: "ri-time-line", hint: t("Нет сообщений и звонков больше трёх месяцев") },
  { id: "tagged", label: t("С тегами"), icon: "ri-price-tag-3-line", hint: t("У контакта есть хотя бы один тег") },
  { id: "country", label: t("Страна известна"), icon: "ri-earth-line", hint: t("Заполнено поле «Страна»") },
  { id: "lang", label: t("Язык известен"), icon: "ri-translate-2", hint: t("Определён язык переписки") },
  { id: "blocked", label: t("Заблокированные"), icon: "ri-forbid-2-line", hint: t("Контакты, которым запрещено писать") },
];

// коды стран для поля «Телефон»: сначала ОАЭ и соседи (рабочее пространство Treejar), первый в списке — код по умолчанию
export const contactCountries = [
  { code: "AE", flag: "🇦🇪", dial: "+971", name: t("ОАЭ") },
  { code: "SA", flag: "🇸🇦", dial: "+966", name: t("Саудовская Аравия") },
  { code: "QA", flag: "🇶🇦", dial: "+974", name: t("Катар") },
  { code: "OM", flag: "🇴🇲", dial: "+968", name: t("Оман") },
  { code: "KW", flag: "🇰🇼", dial: "+965", name: t("Кувейт") },
  { code: "BH", flag: "🇧🇭", dial: "+973", name: t("Бахрейн") },
  { code: "GB", flag: "🇬🇧", dial: "+44", name: t("Великобритания") },
  { code: "IN", flag: "🇮🇳", dial: "+91", name: t("Индия") },
  { code: "PK", flag: "🇵🇰", dial: "+92", name: t("Пакистан") },
  { code: "EG", flag: "🇪🇬", dial: "+20", name: t("Египет") },
  { code: "RU", flag: "🇷🇺", dial: "+7", name: t("Россия") },
  { code: "KZ", flag: "🇰🇿", dial: "+7", name: t("Казахстан") },
  { code: "BY", flag: "🇧🇾", dial: "+375", name: t("Беларусь") },
  { code: "TR", flag: "🇹🇷", dial: "+90", name: t("Турция") },
  { code: "IT", flag: "🇮🇹", dial: "+39", name: t("Италия") },
  { code: "DE", flag: "🇩🇪", dial: "+49", name: t("Германия") },
  { code: "FR", flag: "🇫🇷", dial: "+33", name: t("Франция") },
  { code: "ES", flag: "🇪🇸", dial: "+34", name: t("Испания") },
  { code: "US", flag: "🇺🇸", dial: "+1", name: t("США") },
  { code: "CN", flag: "🇨🇳", dial: "+86", name: t("Китай") },
];
export const DEFAULT_COUNTRY = contactCountries[0];

export const contactEmpty = {
  title: t("В этом рабочем пространстве нет контактов"),
  text: t("Добавьте один контакт или загрузите CSV-файл, чтобы импортировать несколько контактов сразу."),
};

export const contactsAppHints = [
  {
    icon: "ri-contacts-book-2-line",
    title: t("Контакты — общая база"),
    text: t("Один клиент — одна карточка, даже если он писал в WhatsApp, Telegram и на почту."),
  },
  {
    icon: "ri-filter-3-line",
    title: t("Папки вместо фильтров"),
    text: t("Этапы жизненного цикла и сегменты сразу считают, сколько контактов подходит под условие."),
  },
  {
    icon: "ri-upload-cloud-2-line",
    title: t("Импорт и экспорт"),
    text: t("CSV-файл загружается в браузере, а выгрузку можно запросить в «Экспорте данных»."),
  },
];

/** цвет значка этапа: потерянные — серые, «выигрыш» — основной цвет, остальные по иконке */
export const STAGE_TONE: Record<string, string> = {
  neutral: "bg-muted text-muted-foreground",
  hot: "bg-amber-400/20 text-amber-600",
  pay: "bg-emerald-500/15 text-emerald-600",
  client: "bg-primary/15 text-primary",
  lost: "bg-foreground/10 text-muted-foreground",
};

export const stageTone = (s: Pick<Stage, "lost" | "isWon" | "isDefault" | "icon">) =>
  STAGE_TONE[s.lost ? "lost" : s.isWon ? "client" : s.isDefault ? "neutral" : /coin|money|bank|wallet/.test(s.icon) ? "pay" : "hot"];

/** человек, которому можно назначить контакт */
export type ContactOwner = { id: string; name: string; role: string };

/** «5 мин назад», «вчера», «4 дня назад», «12 мар» */
export function relTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const sec = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (sec < 60) return t("только что");
  if (sec < 3600) return t("{n} мин назад", { n: Math.floor(sec / 60) });
  if (sec < 86400) return t("{n} ч назад", { n: Math.floor(sec / 3600) });
  const days = Math.floor(sec / 86400);
  if (days === 1) return t("Вчера");
  if (days < 7) return t("{d} назад", { d: tn(days, "день", "дня", "дней") });
  return d.toLocaleDateString(locale, { day: "numeric", month: "short", year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric" });
}

export const plural = (n: number, one: string, few: string, many: string) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
};
